import React, { useMemo, useReducer } from 'react'
import Skeleton from 'react-loading-skeleton'

import classnames from 'classnames'

// eslint-disable-next-line camelcase
import {
  CatalogProductType,
  DoProduct as GraphQLDoProduct,
  Menus_ContentAdvisories,
  Menus_MenuItemTag,
  StockInformation,
  TimeBasedRules,
  UnitOfMeasure
} from 'src/apollo/onlineOrdering'
import {
  CardOrientation,
  MenuConfig,
  MenuTemplate,
  PlaceHolderImageGenerationEnum,
  Theme
} from 'src/apollo/sites'
import useOnVisible from 'src/lib/js/hooks/useOnVisible'
import { isLevelAA } from 'src/shared/js/colorUtils'

import Image from 'shared/components/common/Image'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { DEFAULT_COLORS } from 'public/components/default_template/meta/StyleMeta'
import { ToastImageSet } from 'public/components/default_template/product_display/MenuItemImage'
import { OfferBadge } from 'public/components/default_template/online_ordering/item_tags/MenuItemTags'
import { useOffers } from 'public/components/online_ordering/OffersContext'

import MenuItemCard, { getTemplateClass } from './MenuItemCard'
import { MenuSectionHeader } from './MenuSectionHeader'
import {
  overrideConfigBasedOnTemplate,
  TemplateOverrides
} from './menuStylingOverrides'
import { ItemSource } from 'public/components/default_template/online_ordering/cart/cartUtils'

export type MenuItem = {
  defaultModifiersPrice?: number | null
  name?: string | null
  image?: string | null
  imageUrls?: { [key: string]: string } | null
  imageSetUrls?: ToastImageSet[] | null
  price?: number | null
  prices?: number[] | null
  description?: string | null
  guid: string | null
  outOfStock?: boolean | null
  itemGroupGuid: string
  timeBasedRules?: TimeBasedRules | null
  // eslint-disable-next-line camelcase
  itemTags?: Menus_MenuItemTag[]
  masterId?: string | null
  unitOfMeasure?: UnitOfMeasure | null
  usesFractionalQuantity?: boolean | null
  stockAcrossLocations?: StockInformation[] | null
  slug?: string | null
  hasModifiers?: boolean | null
  contentAdvisories?: Menus_ContentAdvisories | null
  parentSlugs?: Array<{ name: string; slug: string }> | null
}

// Create a compatible DoProduct type that works with our MenuItem
export type DoProduct = Omit<GraphQLDoProduct, 'variants'> & {
  variants: MenuItem[]
}

export type Props = {
  header?: string | null
  description?: string | null
  menuItems?: (MenuItem | null)[] | null
  products?: (DoProduct | null)[] | null
  onVisible?: () => void
  restaurantGuid?: string
  setIsMapOrModalOpen?: (isOpen: boolean) => void
  isMapOrModalOpen?: boolean
  orientation?: CardOrientation | null
  isReadOnly?: boolean
  menuConfig?: MenuConfig | null
  wrapItems?: boolean
  menuItemIdMap?: Map<number, string>
  groupGuid?: string
  menuGuid?: string
  channelGuid?: string
  loading?: boolean
  itemSource?: ItemSource
  variableMenuSpacing?: boolean
}

type MenuItemCardsProps = {
  menuItems?: (MenuItem | null)[] | null
  products?: (DoProduct | null)[] | null
  menuItemIdMap?: Map<number, string>
  menuGuid?: string | null
  setIsMapOrModalOpen?: (isOpen: boolean) => void
  isMapOrModalOpen?: boolean
  isReadOnly?: boolean
  menuConfig?: MenuConfig | null
  templateOverrides?: TemplateOverrides
  channelGuid?: string
  itemSource?: ItemSource
  loading?: boolean
}

export const MenuItemCards = ({
  menuItems,
  products,
  menuGuid,
  menuItemIdMap,
  setIsMapOrModalOpen,
  isMapOrModalOpen,
  isReadOnly,
  menuConfig,
  templateOverrides,
  channelGuid,
  itemSource,
  loading
}: MenuItemCardsProps) => {
  // Build a list of MenuItems and DoProducts
  const displayItems = React.useMemo(() => {
    const items: Array<{ item: MenuItem; product?: DoProduct }> = []

    // Add products with their first variant as the display item
    if (products) {
      products
        .filter((product) => product !== null)
        .forEach((product) => {
          const firstVariant = product.variants[0]
          if (firstVariant) {
            // Coerce the DoProduct into a MenuItem shape for the MenuItemCard to display
            const displayItem: MenuItem = {
              ...firstVariant,
              // Display the product name instead of the variant name
              name: product.name ?? firstVariant.name,
              description: product.description ?? firstVariant.description,
              itemGroupGuid:
                product.itemGroupGuid ?? firstVariant.itemGroupGuid,
              // Display the product image urls if present, otherwise pick the image of the first variant
              imageUrls: product.imageUrls ?? firstVariant.imageUrls,
              prices: product.prices ?? firstVariant.prices,
              outOfStock: product.outOfStock ?? firstVariant.outOfStock,
              itemTags: product.itemTags ?? firstVariant.itemTags,
              // For OPTION products, use product slug; for SINGLE products, use variant slug
              slug:
                product.type === CatalogProductType.Option
                  ? product.slug ?? firstVariant.slug
                  : firstVariant.slug
            }
            items.push({ item: displayItem, product })
          }
        })
    }

    if (menuItems) {
      menuItems
        .filter((menuItem) => menuItem !== null)
        .forEach((menuItem) => items.push({ item: menuItem }))
    }

    return items
  }, [menuItems, products])

  return (
    <>
      {loading || !displayItems.length
        ? new Array(8).fill(0).map((_, index) => (
            <li
              key={`skeleton${index}`}
              className={classnames(
                'item menu-item-skeleton',
                ...(loading
                  ? [
                      getTemplateClass(templateOverrides?.menuTemplateOverride),
                      {
                        mobileOptimized:
                          templateOverrides?.enableMobileOptimization
                      }
                    ]
                  : [])
              )}
              data-testid='menu-item-skeleton'
            >
              <Skeleton
                width='100%'
                height='100%'
                containerClassName={loading ? 'skeletonFill' : undefined}
              />
            </li>
          ))
        : displayItems.map(({ item, product }, i) => (
            <MenuItemCard
              key={`${item.guid}${item.name}${i}`}
              id={menuItemIdMap?.get(i)}
              item={item}
              product={product}
              menuGuid={menuGuid}
              setIsMapOrModalOpen={setIsMapOrModalOpen}
              isMapOrModalOpen={isMapOrModalOpen}
              isReadOnly={isReadOnly}
              menuConfig={menuConfig}
              channelGuid={channelGuid}
              templateOverrides={templateOverrides}
              itemSource={itemSource}
            />
          ))}
    </>
  )
}

const getMenuBackgroundStyle = (
  theme: Theme | undefined | null,
  menuConfig: MenuConfig | null | undefined
) => {
  if (theme?.enabled) {
    const menuColorOverrides = theme.colorOverrides?.viewOnlyMenu
    return menuColorOverrides?.cardBackground
      ? { backgroundColor: menuColorOverrides.cardBackground }
      : {}
  } else {
    return menuConfig?.colors?.background
      ? { backgroundColor: menuConfig.colors.background }
      : {}
  }
}

const MenuSection = (props: Props, ref: React.RefObject<HTMLDivElement>) => {
  const {
    restaurant: { meta, config, theme }
  } = useRestaurant()
  const { groupOffers, getOffersTextForEntity } = useOffers()

  const [sectionExpanded, toggleSectionExpanded] = useReducer(
    (state) => !state,
    true
  )
  const templateOverrides = useMemo(
    () => ({
      ...overrideConfigBasedOnTemplate(
        props.menuConfig,
        config,
        props.variableMenuSpacing
      ),
      variableMenuSpacing: props.variableMenuSpacing
    }),
    [config, props.menuConfig, props.variableMenuSpacing]
  )

  useOnVisible(ref, props.onVisible, { rootMargin: '-49% 0px -49% 0px' })

  if (!props.menuItems && !props.products && !props.loading) {
    return null
  }

  const menuFormatConfig = props.menuConfig?.format

  const hasPlaceholderImages =
    menuFormatConfig?.placeholderImage &&
    menuFormatConfig.placeholderImage.generatePlaceholderImage !==
      PlaceHolderImageGenerationEnum.None

  // Check all items (both products and menuItems) for images
  const noOriginalImages =
    (props.menuItems?.every(
      (item) => !item?.image && !item?.imageUrls?.medium
    ) ??
      true) &&
    (props.products?.every(
      (product) =>
        !product?.imageLink &&
        !product?.imageUrls?.medium &&
        !product?.variants?.[0]?.imageUrls?.medium
    ) ??
      true)
  const noImages = noOriginalImages && !hasPlaceholderImages
  templateOverrides.hideImages =
    noOriginalImages && menuFormatConfig?.template === MenuTemplate.TopImageV2

  const hasLowContrast = !isLevelAA(
    meta.secondaryTextColor || DEFAULT_COLORS.secondaryText,
    meta.backgroundColor || DEFAULT_COLORS.background
  )

  if (
    menuFormatConfig?.template === MenuTemplate.Condensed ||
    menuFormatConfig?.template === MenuTemplate.ExtraCondensed ||
    menuFormatConfig?.template === MenuTemplate.Stacked
  ) {
    const sectionStyle = getMenuBackgroundStyle(theme, props.menuConfig)

    return (
      <div className='menuGroup'>
        <section
          {...(props.groupGuid && {
            'aria-describedby': `menu-pill-${props.groupGuid}`
          })}
          aria-labelledby={
            props.header && props.groupGuid
              ? `menuGroup-${props.groupGuid}-heading`
              : undefined
          }
          className={classnames(
            'menuSection',
            getTemplateClass(menuFormatConfig?.template),
            { card: menuFormatConfig?.expandableSections }
          )}
          ref={ref}
          tabIndex={-1}
          id={`menuGroup-${props.groupGuid}`}
        >
          <div className='menuSectionHeaderWrapper'>
            {menuFormatConfig?.expandableSections ? (
              <button
                className={classnames('menuSectionHeader', 'button')}
                onClick={toggleSectionExpanded}
                aria-expanded={Boolean(sectionExpanded)}
                aria-controls={`menuGroup-${props.groupGuid}-section`}
              >
                <MenuSectionHeader
                  header={props.header}
                  headingId={`menuGroup-${props.groupGuid}-heading`}
                />
                <Image
                  src='icons/caret-down-27px.svg'
                  alt=''
                  className={sectionExpanded ? 'expanded' : ''}
                />
              </button>
            ) : (
              <div className={classnames('menuSectionHeader')}>
                <MenuSectionHeader
                  header={props.header}
                  headingId={`menuGroup-${props.groupGuid}-heading`}
                />
              </div>
            )}
            {props.description && (
              <div
                className={classnames('description', {
                  primaryTextColor: hasLowContrast
                })}
              >
                {props.description}
              </div>
            )}
            {menuFormatConfig?.columns && (
              <div className='sectionSeparatorWrapper'>
                <hr className='sectionSeparator' />
              </div>
            )}
          </div>
          {(props.products || props.menuItems) && (
            <div
              style={sectionStyle}
              id={`menuGroup-${props.groupGuid}-section`}
              className={classnames('itemSection', {
                expanded:
                  !menuFormatConfig?.expandableSections || sectionExpanded,
                squaredCorners: menuFormatConfig?.squaredCorners
              })}
            >
              {!menuFormatConfig?.columns && (
                <div className='sectionSeparatorWrapper' aria-hidden={true}>
                  <hr className='sectionSeparator' />
                </div>
              )}
              <ul
                style={sectionStyle}
                className={classnames('itemSection', {
                  columns: menuFormatConfig?.columns,
                  expanded:
                    !menuFormatConfig?.expandableSections || sectionExpanded
                })}
              >
                <MenuItemCards
                  menuItems={props.menuItems}
                  products={props.products}
                  menuItemIdMap={props.menuItemIdMap}
                  menuGuid={props.menuGuid}
                  setIsMapOrModalOpen={props.setIsMapOrModalOpen}
                  isMapOrModalOpen={props.isMapOrModalOpen}
                  isReadOnly={props.isReadOnly}
                  menuConfig={props.menuConfig}
                  channelGuid={props.channelGuid}
                  templateOverrides={templateOverrides}
                  itemSource={props.itemSource}
                  loading={props.loading}
                />
              </ul>
            </div>
          )}
        </section>
      </div>
    )
  }

  const condenseGroups = menuFormatConfig?.condenseImagelessGroups && noImages
  const templateClass = condenseGroups
    ? ''
    : getTemplateClass(menuFormatConfig?.template)
  const isNewTemplate = menuFormatConfig?.template
    ? [
        MenuTemplate.Classic,
        MenuTemplate.LeftImageV2,
        MenuTemplate.TopImageV2
      ].includes(menuFormatConfig.template)
    : false
  const sectionStyle =
    condenseGroups && !isNewTemplate
      ? getMenuBackgroundStyle(theme, props.menuConfig)
      : {}
  const numGroupOffers = (
    props.groupGuid ? groupOffers[props.groupGuid] ?? [] : []
  ).length
  const numTotalOffers = getOffersTextForEntity(
    undefined,
    props.groupGuid,
    props.menuGuid
  ).length

  return (
    <div className='menuGroup'>
      <section
        tabIndex={-1}
        {...(props.groupGuid && {
          'aria-describedby': `menu-pill-${props.groupGuid}`
        })}
        aria-labelledby={
          props.header && props.groupGuid
            ? `menuGroup-${props.groupGuid}-heading`
            : undefined
        }
        className={classnames('menuSection', templateClass, {
          card: menuFormatConfig?.expandableSections
        })}
        ref={ref}
        id={`menuGroup-${props.groupGuid}`}
      >
        {props.header || props.description ? (
          <div className='headerWrapper'>
            <MenuSectionHeader
              header={props.header}
              headingId={`menuGroup-${props.groupGuid}-heading`}
              badge={
                numGroupOffers > 0 ? (
                  <OfferBadge text={numTotalOffers > 1 ? 'OFFERS' : 'OFFER'} />
                ) : null
              }
            />
            {props.description && (
              <div
                className={classnames('description', {
                  primaryTextColor: hasLowContrast
                })}
              >
                {props.description}
              </div>
            )}
          </div>
        ) : null}
        <ul
          style={sectionStyle}
          className={classnames('itemSection', {
            modalOpen: props.isMapOrModalOpen,
            compact: noImages && !isNewTemplate,
            listView:
              menuFormatConfig?.condenseImagelessGroups && !isNewTemplate,
            squaredCorners: menuFormatConfig?.squaredCorners,
            mobileOptimizedContainer:
              templateOverrides.enableMobileOptimization,
            topImageV2: menuFormatConfig?.template === MenuTemplate.TopImageV2
          })}
          aria-busy={
            props.loading ?? !(props.menuItems || props.products)
              ? 'true'
              : 'false'
          }
        >
          <MenuItemCards
            menuItems={props.menuItems}
            products={props.products}
            menuGuid={props.menuGuid}
            menuItemIdMap={props.menuItemIdMap}
            setIsMapOrModalOpen={props.setIsMapOrModalOpen}
            isMapOrModalOpen={props.isMapOrModalOpen}
            isReadOnly={props.isReadOnly}
            menuConfig={props.menuConfig}
            channelGuid={props.channelGuid}
            templateOverrides={templateOverrides}
            itemSource={props.itemSource}
            loading={props.loading}
          />
        </ul>
      </section>
    </div>
  )
}

export default React.forwardRef(MenuSection)
